// 80mm thermal-friendly HTML receipt template
// Uses CSS that fits well on 80mm thermal printers (~72mm printable width)
// Keep it monochrome/no images so it prints fast and clean.

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[c]));

export function buildReceiptHTML(order, restaurant) {
  const lines = (order.items || []).map(it => {
    const name = escapeHtml(it.name || '');
    const qty = it.quantity || 1;
    const subtotal = ((it.price || 0) * qty).toFixed(2);
    return `<tr>
      <td style="text-align:left;vertical-align:top;">${qty}x ${name}</td>
      <td style="text-align:right;vertical-align:top;white-space:nowrap;">EUR ${subtotal}</td>
    </tr>`;
  }).join('');

  const created = order.created_at ? new Date(order.created_at) : new Date();
  const dateStr = created.toLocaleString('pt-PT');
  const orderShort = (order.order_id || '').slice(-8).toUpperCase();
  const restName = escapeHtml(restaurant?.name || 'WEGOO');
  const restPhone = escapeHtml(restaurant?.phone || '');

  const customerName = escapeHtml(order.customer_name || '');
  const customerPhone = escapeHtml(order.customer_phone || '');
  const deliveryAddr = escapeHtml(order.delivery_address || '');
  const notes = escapeHtml(order.notes || '');
  const paymentLabel = order.payment_method === 'cash' ? 'DINHEIRO' : 'CARTAO';
  const paymentStatus = order.payment_status === 'paid' ? '(PAGO)' : '(POR PAGAR)';

  const total = (order.total || 0).toFixed(2);
  const subtotal = (order.subtotal || (order.total - (order.delivery_fee || 0))).toFixed(2);
  const deliveryFee = (order.delivery_fee || 0).toFixed(2);

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>Pedido ${orderShort}</title>
<style>
  @page { size: 80mm auto; margin: 2mm; }
  * { box-sizing: border-box; }
  body {
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px;
    width: 76mm;
    margin: 0;
    padding: 2mm;
    color: #000;
    line-height: 1.35;
  }
  h1 { font-size: 16px; text-align: center; margin: 0 0 4px; letter-spacing: 1px; }
  h2 { font-size: 13px; text-align: center; margin: 4px 0; }
  .center { text-align: center; }
  .right { text-align: right; }
  .bold { font-weight: bold; }
  .big { font-size: 14px; font-weight: bold; }
  .sep { border-top: 1px dashed #000; margin: 4px 0; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 1px 0; vertical-align: top; }
  .item-name { font-weight: bold; }
  .small { font-size: 10px; }
  .order-num { font-size: 22px; font-weight: bold; text-align: center; letter-spacing: 2px; }
  @media print {
    body { width: 76mm; }
  }
</style>
</head>
<body>
  <h1>WEGOO</h1>
  <div class="center small">Troia Delivery</div>
  <div class="sep"></div>

  <h2>${restName}</h2>
  ${restPhone ? `<div class="center small">Tel: ${restPhone}</div>` : ''}

  <div class="sep"></div>
  <div class="order-num">#${orderShort}</div>
  <div class="center small">${dateStr}</div>
  <div class="sep"></div>

  <table>
    ${lines}
  </table>

  <div class="sep"></div>
  <table>
    <tr><td>Subtotal</td><td class="right">EUR ${subtotal}</td></tr>
    <tr><td>Entrega</td><td class="right">EUR ${deliveryFee}</td></tr>
    <tr><td class="big">TOTAL</td><td class="right big">EUR ${total}</td></tr>
  </table>

  <div class="sep"></div>
  <div><span class="bold">Pagamento:</span> ${paymentLabel} ${paymentStatus}</div>

  <div class="sep"></div>
  <div class="bold">CLIENTE</div>
  <div>${customerName}</div>
  ${customerPhone ? `<div>Tel: ${customerPhone}</div>` : ''}
  ${deliveryAddr ? `<div class="small">${deliveryAddr}</div>` : ''}

  ${notes ? `<div class="sep"></div><div class="bold">NOTAS:</div><div class="small">${notes}</div>` : ''}

  <div class="sep"></div>
  <div class="center small">Obrigado pela preferencia!</div>
  <div class="center small">www.wegoo.pt</div>
  <br/>
  <script>
    // Keep this file simple. Parent calls window.print().
  </script>
</body></html>`;
}


/**
 * Daily closing summary — when admin closes in ALL mode, prints **one
 * receipt per restaurant** (separated by page break) plus a final receipt
 * with the WEGOO grand total. Manager flow gets a single receipt with no
 * commission/net info.
 */
export function buildCloseDayReceiptHTML(summary) {
  const closedAt = new Date(summary.closed_at).toLocaleString('pt-PT');
  const dayStart = new Date(summary.day_start).toLocaleDateString('pt-PT');
  const isAdmin = summary.viewer_role !== 'restaurant_manager';

  const restPage = (s, idx) => `
    <section class="page" style="${idx > 0 ? 'page-break-before: always;' : ''}">
      <div class="center bold" style="font-size:18px;">FECHO DE DIA</div>
      <div class="center" style="font-size:14px;">${escapeHtml(dayStart)}</div>
      <div class="center" style="font-size:11px;">${escapeHtml(closedAt)}</div>
      <div class="sep"></div>
      <div class="bold center" style="font-size:16px;">${escapeHtml(s.restaurant_name)}</div>
      <div class="sep"></div>
      <table style="width:100%;font-size:13px;">
        <tr><td>Pedidos entregues</td><td style="text-align:right;">${s.delivered}</td></tr>
        <tr><td>Cancelados</td><td style="text-align:right;">${s.cancelled}</td></tr>
        <tr><td>Subtotal (comida)</td><td style="text-align:right;">EUR ${s.subtotal.toFixed(2)}</td></tr>
        <tr><td>Taxas de entrega</td><td style="text-align:right;">EUR ${s.delivery_fees.toFixed(2)}</td></tr>
        <tr><td><b>Total faturado</b></td><td style="text-align:right;"><b>EUR ${s.total.toFixed(2)}</b></td></tr>
        ${isAdmin ? `
          <tr><td>WEGOO 15%</td><td style="text-align:right;">- EUR ${(s.commission || 0).toFixed(2)}</td></tr>
          <tr><td><b>Liquido</b></td><td style="text-align:right;"><b>EUR ${(s.net || 0).toFixed(2)}</b></td></tr>
        ` : ''}
      </table>
      <div class="sep"></div>
      <div class="center" style="font-size:11px;">www.wegoo.pt</div>
    </section>
  `;

  const sections = (summary.by_restaurant || []).map(restPage).join('');

  const ice = summary.ice_summary || { orders: 0, total: 0 };
  const shisha = summary.shisha_summary || { orders: 0, total: 0 };
  const hasServices = (ice.orders > 0 || shisha.orders > 0);

  // Admin gets an extra final page with the WEGOO grand total (private).
  let adminFooter = '';
  if (isAdmin && ((summary.by_restaurant || []).length > 0 || hasServices)) {
    const g = summary.grand_total || {};
    const dayTotal = (g.day_total != null) ? g.day_total : (g.total ?? 0);
    adminFooter = `
      <section class="page" style="page-break-before: always;">
        <div class="center bold" style="font-size:18px;">RESUMO WEGOO</div>
        <div class="center" style="font-size:14px;">${escapeHtml(dayStart)}</div>
        <div class="sep"></div>
        <div class="bold center" style="font-size:14px;">RESTAURANTES (COMIDA)</div>
        <div class="sep"></div>
        <table style="font-size:13px;width:100%;">
          <tr><td>Pedidos entregues</td><td style="text-align:right;">${g.delivered ?? 0}</td></tr>
          <tr><td>Cancelados</td><td style="text-align:right;">${g.cancelled ?? 0}</td></tr>
          <tr><td>Faturacao bruta</td><td style="text-align:right;">EUR ${(g.subtotal ?? 0).toFixed(2)}</td></tr>
          <tr><td>Taxas entrega</td><td style="text-align:right;">EUR ${(g.delivery_fees ?? 0).toFixed(2)}</td></tr>
          <tr><td>Total comida</td><td style="text-align:right;">EUR ${(g.total ?? 0).toFixed(2)}</td></tr>
          <tr><td>Comissao 15%</td><td style="text-align:right;">EUR ${(g.commission ?? 0).toFixed(2)}</td></tr>
          <tr><td><b>Liquido a pagar</b></td><td style="text-align:right;"><b>EUR ${(g.net ?? 0).toFixed(2)}</b></td></tr>
        </table>
        <div class="sep"></div>
        <div class="bold center" style="font-size:14px;">SERVICOS</div>
        <table style="font-size:13px;width:100%;">
          <tr><td>Gelo (${ice.orders})</td><td style="text-align:right;">EUR ${(ice.total || 0).toFixed(2)}</td></tr>
          <tr><td>Shisha (${shisha.orders})</td><td style="text-align:right;">EUR ${(shisha.total || 0).toFixed(2)}</td></tr>
        </table>
        <div class="sep"></div>
        <table style="font-size:14px;width:100%;">
          <tr><td><b>TOTAL GERAL DO DIA</b></td><td style="text-align:right;"><b>EUR ${dayTotal.toFixed(2)}</b></td></tr>
        </table>
        <div class="sep"></div>
        <div class="center" style="font-size:10px;">--- INTERNO WEGOO ---</div>
      </section>
    `;
  }

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>FECHO DIA</title>
<style>
  @page { size: 80mm auto; margin: 4mm; }
  body { font-family: 'Courier New', monospace; font-size: 13px; width: 72mm; margin: 0; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .sep { border-bottom: 1px dashed #000; margin: 6px 0; }
  table { width: 100%; }
  .page { padding-bottom: 8px; }
</style></head>
<body>
  ${sections}
  ${adminFooter}
</body></html>`;
}

